import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AlertCircle, CalendarDays, Check, Copy, Search } from 'lucide-react';

const buildMonthCells = (viewYear, viewMonth) => {
  const cells = [];
  const first = new Date(viewYear, viewMonth, 1);
  const startPad = first.getDay();
  let day = 1 - startPad;
  for (let i = 0; i < 42; i++) {
    const cellDate = new Date(viewYear, viewMonth, day);
    cells.push({
      date: cellDate,
      inMonth: cellDate.getMonth() === viewMonth
    });
    day++;
  }
  return cells;
};

const isSameCalendarDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const buildDecadeYearItems = (decadeStart) => {
  const out = [];
  for (let i = 0; i < 12; i++) {
    const year = decadeStart - 1 + i;
    out.push({ year, faded: i === 0 || i === 11 });
  }
  return out;
};

const buildCenturyDecadeItems = (centuryBase) => {
  const items = [];
  items.push({
    label: `${centuryBase - 10}-${centuryBase - 1}`,
    decadeStart: centuryBase - 10,
    faded: true
  });
  for (let i = 0; i < 10; i++) {
    const s = centuryBase + i * 10;
    items.push({ label: `${s}-${s + 9}`, decadeStart: s, faded: false });
  }
  items.push({
    label: `${centuryBase + 100}-${centuryBase + 109}`,
    decadeStart: centuryBase + 100,
    faded: true
  });
  return items;
};

const hoverHeader = {
  cursor: 'pointer',
  borderRadius: '4px',
  padding: '2px 4px',
  transition: 'background 0.12s ease'
};

function CalendarDropdown({ open, anchorDate, onPickDay, selectedDate }) {
  const [mode, setMode] = useState('days');
  const [viewDate, setViewDate] = useState(() => new Date(anchorDate));
  const [decadeBase, setDecadeBase] = useState(() => Math.floor(new Date(anchorDate).getFullYear() / 10) * 10);
  const [centuryBase, setCenturyBase] = useState(() => Math.floor(new Date(anchorDate).getFullYear() / 100) * 100);

  useEffect(() => {
    if (!open) return;
    const d = new Date(anchorDate);
    setMode('days');
    setViewDate(d);
    const y = d.getFullYear();
    setDecadeBase(Math.floor(y / 10) * 10);
    setCenturyBase(Math.floor(y / 100) * 100);
  }, [open, anchorDate]);

  const setHoverBg = useCallback((el, on) => {
    if (el) el.style.background = on ? '#d8efff' : 'transparent';
  }, []);

  if (!open) return null;

  const today = new Date();
  const navBtn = {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '18px',
    color: '#245982',
    padding: '2px 8px',
    lineHeight: 1
  };

  const shell = {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: '6px',
    zIndex: 100,
    background: 'white',
    border: '1px solid #ccc',
    borderRadius: '6px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    padding: '10px',
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontSize: '13px',
    color: '#222',
    minWidth: mode === 'days' ? '260px' : '280px'
  };

  const grid4 = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '6px',
    marginTop: '8px'
  };

  const goMonths = () => setMode('months');
  const goYears = () => {
    const y = viewDate.getFullYear();
    setDecadeBase(Math.floor(y / 10) * 10);
    setMode('years');
  };
  const goDecades = () => {
    const y = viewDate.getFullYear();
    setCenturyBase(Math.floor(y / 100) * 100);
    setMode('decades');
  };

  const footerToday = (
    <div style={{ marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #eee', fontSize: '12px', color: '#245982' }}>
      Today: {today.getMonth() + 1}/{today.getDate()}/{today.getFullYear()}
    </div>
  );

  if (mode === 'decades') {
    const items = buildCenturyDecadeItems(centuryBase);
    const title = `${centuryBase}-${centuryBase + 99}`;
    return (
      <div style={shell}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
          <button type="button" aria-label="Previous century" style={navBtn} onClick={() => setCenturyBase((c) => c - 100)}>
            ‹
          </button>
          <span style={{ fontWeight: 'bold', color: '#245982' }}>{title}</span>
          <button type="button" aria-label="Next century" style={navBtn} onClick={() => setCenturyBase((c) => c + 100)}>
            ›
          </button>
        </div>
        <div style={grid4}>
          {items.map((item, idx) => {
            const selDecade =
              selectedDate &&
              selectedDate.getFullYear() >= item.decadeStart &&
              selectedDate.getFullYear() <= item.decadeStart + 9;
            return (
              <button
                key={`${item.label}-${idx}`}
                type="button"
                onClick={() => {
                  setDecadeBase(item.decadeStart);
                  setViewDate(new Date(item.decadeStart, 0, 1));
                  setMode('years');
                }}
                style={{
                  border: selDecade && !item.faded ? '2px dashed #245982' : '1px solid #ddd',
                  background: selDecade && !item.faded ? '#d8efff' : item.faded ? '#f5f5f5' : 'white',
                  borderRadius: '4px',
                  padding: '8px 4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: item.faded ? '#aaa' : '#222',
                  fontFamily: 'Arial, Helvetica, sans-serif'
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
        {footerToday}
      </div>
    );
  }

  if (mode === 'years') {
    const items = buildDecadeYearItems(decadeBase);
    const title = `${decadeBase}-${decadeBase + 9}`;
    return (
      <div style={shell}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
          <button type="button" aria-label="Previous decade" style={navBtn} onClick={() => setDecadeBase((d) => d - 10)}>
            ‹
          </button>
          <button
            type="button"
            onClick={goDecades}
            onMouseEnter={(e) => setHoverBg(e.currentTarget, true)}
            onMouseLeave={(e) => setHoverBg(e.currentTarget, false)}
            style={{ ...hoverHeader, fontWeight: 'bold', color: '#245982', border: 'none', background: 'transparent', fontSize: '14px' }}
          >
            {title}
          </button>
          <button type="button" aria-label="Next decade" style={navBtn} onClick={() => setDecadeBase((d) => d + 10)}>
            ›
          </button>
        </div>
        <div style={grid4}>
          {items.map((item) => {
            const isSel = selectedDate && selectedDate.getFullYear() === item.year;
            return (
              <button
                key={item.year}
                type="button"
                onClick={() => {
                  setViewDate(new Date(item.year, viewDate.getMonth(), 1));
                  setMode('months');
                }}
                style={{
                  border: isSel ? '2px solid #245982' : '1px solid #eee',
                  background: isSel ? '#e3f2fd' : item.faded ? '#f9f9f9' : 'white',
                  borderRadius: '4px',
                  padding: '8px 4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: item.faded ? '#aaa' : '#222',
                  fontWeight: isSel ? 'bold' : 'normal',
                  fontFamily: 'Arial, Helvetica, sans-serif'
                }}
              >
                {item.year}
              </button>
            );
          })}
        </div>
        {footerToday}
      </div>
    );
  }

  if (mode === 'months') {
    const vy = viewDate.getFullYear();
    return (
      <div style={shell}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
          <button type="button" aria-label="Previous year" style={navBtn} onClick={() => setViewDate(new Date(vy - 1, viewDate.getMonth(), 1))}>
            ‹
          </button>
          <button
            type="button"
            onClick={goYears}
            onMouseEnter={(e) => setHoverBg(e.currentTarget, true)}
            onMouseLeave={(e) => setHoverBg(e.currentTarget, false)}
            style={{ ...hoverHeader, fontWeight: 'bold', color: '#245982', border: 'none', background: 'transparent', fontSize: '16px' }}
          >
            {vy}
          </button>
          <button type="button" aria-label="Next year" style={navBtn} onClick={() => setViewDate(new Date(vy + 1, viewDate.getMonth(), 1))}>
            ›
          </button>
        </div>
        <div style={grid4}>
          {MONTH_SHORT.map((name, mi) => {
            const isTodayMonth = today.getFullYear() === vy && today.getMonth() === mi;
            const isSel = selectedDate && selectedDate.getFullYear() === vy && selectedDate.getMonth() === mi;
            return (
              <button
                key={name}
                type="button"
                onClick={() => {
                  setViewDate(new Date(vy, mi, 1));
                  setMode('days');
                }}
                style={{
                  border: isSel ? '2px solid #245982' : '1px solid #eee',
                  background: isSel ? '#e3f2fd' : isTodayMonth ? '#d8efff' : 'white',
                  borderRadius: '4px',
                  padding: '8px 4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#222',
                  fontWeight: isTodayMonth || isSel ? 'bold' : 'normal',
                  fontFamily: 'Arial, Helvetica, sans-serif'
                }}
              >
                {name}
              </button>
            );
          })}
        </div>
        {footerToday}
      </div>
    );
  }

  const y = viewDate.getFullYear();
  const mo = viewDate.getMonth();
  const cells = buildMonthCells(y, mo);
  const monthName = MONTH_LONG[mo];

  return (
    <div style={shell}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <button type="button" aria-label="Previous month" style={navBtn} onClick={() => setViewDate(new Date(y, mo - 1, 1))}>
          ‹
        </button>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            flex: 1,
            flexWrap: 'wrap'
          }}
        >
          <button
            type="button"
            onClick={goMonths}
            onMouseEnter={(e) => setHoverBg(e.currentTarget, true)}
            onMouseLeave={(e) => setHoverBg(e.currentTarget, false)}
            style={{ ...hoverHeader, border: 'none', background: 'transparent', fontWeight: 'bold', color: '#245982', fontSize: '14px' }}
          >
            {monthName}
          </button>
          <button
            type="button"
            onClick={goMonths}
            onMouseEnter={(e) => setHoverBg(e.currentTarget, true)}
            onMouseLeave={(e) => setHoverBg(e.currentTarget, false)}
            style={{ ...hoverHeader, border: 'none', background: 'transparent', fontWeight: 'bold', color: '#245982', fontSize: '14px' }}
          >
            {y}
          </button>
        </div>
        <button type="button" aria-label="Next month" style={navBtn} onClick={() => setViewDate(new Date(y, mo + 1, 1))}>
          ›
        </button>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '2px',
          textAlign: 'center',
          marginBottom: '6px',
          fontSize: '11px',
          color: '#666'
        }}
      >
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
        {cells.map((cell, i) => {
          const isToday = isSameCalendarDay(cell.date, today);
          const isSel = selectedDate && isSameCalendarDay(cell.date, selectedDate);
          return (
            <button
              key={i}
              type="button"
              onClick={() => onPickDay(cell.date)}
              style={{
                border: isSel ? '2px solid #245982' : '1px solid transparent',
                background: isToday ? '#d8efff' : isSel ? '#e3f2fd' : 'transparent',
                borderRadius: '4px',
                padding: '5px 0',
                cursor: 'pointer',
                color: cell.inMonth ? '#222' : '#bbb',
                fontSize: '13px',
                fontFamily: 'Arial, Helvetica, sans-serif',
                fontWeight: isToday ? 'bold' : 'normal'
              }}
            >
              {cell.date.getDate()}
            </button>
          );
        })}
      </div>
      {footerToday}
    </div>
  );
}

export default function CFSSCalculator() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);
  const [activeTab, setActiveTab] = useState('calculator');
  const [agencySearch, setAgencySearch] = useState('');
  const [copiedAgencyField, setCopiedAgencyField] = useState('');
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState('');
  const [agencyLoadError, setAgencyLoadError] = useState('');

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetch('/agency-list.json')
      .then(response => {
        if (!response.ok) throw new Error('Unable to load agency list');
        return response.json();
      })
      .then(parsedAgencies => {
        if (cancelled) return;
        setAgencies(parsedAgencies);
        setSelectedAgencyId(parsedAgencies[0]?.id || '');
        setAgencyLoadError('');
      })
      .catch(() => {
        if (!cancelled) setAgencyLoadError('Unable to load agency list.');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // DHS Rate lookup table for minutes < 300
  const preCutoffRates = {
    30: 4.913704,
    60: 5.561852,
    75: 5.691481,
    90: 5.777901,
    120: 5.885926,
    150: 5.950741,
    180: 5.993951,
    210: 6.024815,
    225: 6.037160,
    240: 6.047963,
    270: 6.065967
  };

  // Linear interpolation function
  const interpolateRate = (minutes) => {
    const sortedMinutes = Object.keys(preCutoffRates).map(Number).sort((a, b) => a - b);

    let lowerMinute = sortedMinutes[0];
    let upperMinute = sortedMinutes[sortedMinutes.length - 1];

    for (let i = 0; i < sortedMinutes.length - 1; i++) {
      if (minutes >= sortedMinutes[i] && minutes <= sortedMinutes[i + 1]) {
        lowerMinute = sortedMinutes[i];
        upperMinute = sortedMinutes[i + 1];
        break;
      }
    }

    if (minutes <= lowerMinute) {
      return preCutoffRates[lowerMinute];
    }

    if (minutes >= upperMinute) {
      return preCutoffRates[upperMinute];
    }

    const lowerRate = preCutoffRates[lowerMinute];
    const upperRate = preCutoffRates[upperMinute];
    const ratio = (minutes - lowerMinute) / (upperMinute - lowerMinute);

    return lowerRate + (upperRate - lowerRate) * ratio;
  };

  // DHS Rate calculation functions
  const calculateDHSRate = (minutes, isEnhanced, hasQualifyingWorkers) => {
    // Enhanced rate system (≥600 minutes)
    if (minutes >= 600 && isEnhanced) {
      if (hasQualifyingWorkers) {
        return 6.54345679 + (minutes * 0.00010288);
      } else {
        return 6.08345679 + (minutes * 0.00010288);
      }
    }

    // High minutes: Linear formula (≥300 minutes)
    if (minutes >= 300) {
      return 5.98283951 + (minutes * 0.00032922);
    }

    // Low minutes: Lookup table with interpolation (<300 minutes)
    return interpolateRate(minutes);
  };

  const calculateBudgetAmounts = (minutes, totalUnits, usePersonalCare, fmsAnnualFee, goodsServicesCost, persTotal, backgroundStudyFees) => {
    const otherCosts = fmsAnnualFee + goodsServicesCost + persTotal + backgroundStudyFees;

    if (minutes < 600) {
      const rate = calculateDHSRate(minutes, false, false);
      const pureDirectCare = totalUnits * rate;
      const baseTotalBudget = pureDirectCare + 630;
      const actualDirectCare = baseTotalBudget - otherCosts;

      return {
        directCare: actualDirectCare,
        enhancedDirectCare: null,
        nonEnhancedDirectCare: null,
        ratePerUnit: rate,
        baseTotalBudget,
        pureDirectCare
      };
    } else {
      const enhancedRate = calculateDHSRate(minutes, true, true);
      const nonEnhancedRate = calculateDHSRate(minutes, true, false);

      const enhancedPureDirectCare = totalUnits * enhancedRate;
      const baseTotalBudget = enhancedPureDirectCare + 630;

      const enhancedDirectCare = baseTotalBudget - otherCosts;
      const nonEnhancedDirectCare = totalUnits * nonEnhancedRate; // Just units × rate, no deductions

      const actualDirectCare = usePersonalCare ? enhancedDirectCare : nonEnhancedDirectCare;
      const actualRate = usePersonalCare ? enhancedRate : nonEnhancedRate;

      return {
        directCare: actualDirectCare,
        enhancedDirectCare,
        nonEnhancedDirectCare,
        ratePerUnit: actualRate,
        baseTotalBudget,
        pureDirectCare: enhancedPureDirectCare
      };
    }
  };

  // Helper function to convert minutes to hours
  const formatMinutesToHours = (minutes) => {
    if (!minutes || minutes === 0) return { hoursMinutes: '0h0m', decimal: '0.00' };

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const hoursMinutesFormat = `${hours}h${mins}m`;
    const longFormat = mins === 0 ? `${hours} hours` : `${hours} hours ${mins} minutes`;
    const decimalHours = (minutes / 60).toFixed(2);

    return {
      hoursMinutes: hoursMinutesFormat,
      longFormat: longFormat,
      decimal: decimalHours,
      hoursPerWeek: ((minutes * 7) / 60).toFixed(2)
    };
  };

  // Holiday definitions - US Federal Holiday rules
  const getHolidays = (year) => {
    const holidays = [];

    // Fixed dates
    holidays.push({ name: "New Year's Day", date: new Date(year, 0, 1) });
    holidays.push({ name: "Juneteenth", date: new Date(year, 5, 19) });
    holidays.push({ name: "Independence Day", date: new Date(year, 6, 4) });
    holidays.push({ name: "Veterans Day", date: new Date(year, 10, 11) });
    holidays.push({ name: "Christmas Day", date: new Date(year, 11, 25) });

    // Martin Luther King Jr. Day - Third Monday in January
    const januaryFirst = new Date(year, 0, 1);
    const firstMondayJan = new Date(year, 0, 1 + (8 - januaryFirst.getDay()) % 7);
    const mlkDay = new Date(firstMondayJan.getTime() + 14 * 24 * 60 * 60 * 1000);
    holidays.push({ name: "Martin Luther King Jr. Day", date: mlkDay });

    // Memorial Day - Last Monday in May
    const mayLast = new Date(year, 5, 0);
    const memorialDay = new Date(year, 4, mayLast.getDate() - (mayLast.getDay() + 6) % 7);
    holidays.push({ name: "Memorial Day", date: memorialDay });

    // Labor Day - First Monday in September
    const septFirst = new Date(year, 8, 1);
    const laborDay = new Date(year, 8, 1 + (8 - septFirst.getDay()) % 7);
    holidays.push({ name: "Labor Day", date: laborDay });

    // Thanksgiving - Fourth Thursday in November
    const novFirst = new Date(year, 10, 1);
    const firstThursdayNov = new Date(year, 10, 1 + (4 - novFirst.getDay() + 7) % 7);
    const thanksgiving = new Date(firstThursdayNov.getTime() + 21 * 24 * 60 * 60 * 1000);
    holidays.push({ name: "Thanksgiving Day", date: thanksgiving });

    return holidays;
  };

  // Find holidays in date range
  const findHolidaysInRange = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return [];

    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    const holidaysInRange = [];
    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();

    for (let year = startYear; year <= endYear; year++) {
      const yearHolidays = getHolidays(year);

      yearHolidays.forEach(holiday => {
        if (holiday.date >= startDate && holiday.date <= endDate) {
          holidaysInRange.push({
            ...holiday,
            dateStr: holiday.date.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })
          });
        }
      });
    }

    return holidaysInRange.sort((a, b) => a.date - b.date);
  };

  // Get rate method description
  const getRateMethodDescription = () => {
    const minutes = parseInt(minutesPerDay) || 0;
    if (minutes >= 600) {
      return "Enhanced rate system (≥600 min/day)";
    } else if (minutes >= 300) {
      return "Linear formula (≥300 min/day)";
    } else if (minutes > 0) {
      return "Lookup table (<300 min/day)";
    }
    return "";
  };

  // State management
  const [modelType, setModelType] = useState('agency_model');
  const [minutesPerDay, setMinutesPerDay] = useState('');
  const [enhancedRate, setEnhancedRate] = useState(false);
  const [usingAlternativeCare, setUsingAlternativeCare] = useState(false);
  const [usingPersonalCare, setUsingPersonalCare] = useState(true);
  const [startMonth, setStartMonth] = useState('');
  const [startDay, setStartDay] = useState('');
  const [startYear, setStartYear] = useState('2026');
  const [endMonth, setEndMonth] = useState('');
  const [endDay, setEndDay] = useState('');
  const [endYear, setEndYear] = useState('2026');
  const [goodsServicesCost, setGoodsServicesCost] = useState('');
  const [fmsAnnualFee, setFmsAnnualFee] = useState('');
  const [pers, setPers] = useState({ purchase: '', install: '', fee: '', months: '' });
  const [backgroundStudyFees, setBackgroundStudyFees] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [showEnhancedInfo, setShowEnhancedInfo] = useState(false);
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [startDateDraft, setStartDateDraft] = useState('');
  const [endDateDraft, setEndDateDraft] = useState('');
  const [startDateFocused, setStartDateFocused] = useState(false);
  const [endDateFocused, setEndDateFocused] = useState(false);
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);
  const [startCalendarView, setStartCalendarView] = useState(() => new Date());
  const [endCalendarView, setEndCalendarView] = useState(() => new Date());
  const startDateWrapRef = useRef(null);
  const endDateWrapRef = useRef(null);

  // Auto check enhanced rate
  useEffect(() => {
    const minutes = parseInt(minutesPerDay) || 0;
    setEnhancedRate(minutes >= 600);
  }, [minutesPerDay]);

  // When using Alternative Care (AC), reset fields that cannot be used with AC
  useEffect(() => {
    if (usingAlternativeCare) {
      setGoodsServicesCost('0');
      setPers({ purchase: '0', install: '0', fee: '0', months: '0' });
      setUsingPersonalCare(true);
    }
  }, [usingAlternativeCare]);

  // Hide results when model type changes
  useEffect(() => {
    setShowResults(false);
    setResults(null);
  }, [modelType]);

  const formatMMDDDisplay = (month, day, year) => {
    if (!month || !day || !year) return '';
    return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${String(year).padStart(4, '0')}`;
  };

  useEffect(() => {
    if (!startDateFocused && !errors.startDate) {
      setStartDateDraft(formatMMDDDisplay(startMonth, startDay, startYear));
    }
  }, [startMonth, startDay, startYear, startDateFocused, errors.startDate]);

  useEffect(() => {
    if (!endDateFocused && !errors.endDate) {
      setEndDateDraft(formatMMDDDisplay(endMonth, endDay, endYear));
    }
  }, [endMonth, endDay, endYear, endDateFocused, errors.endDate]);

  useEffect(() => {
    if (!startPickerOpen && !endPickerOpen) return;
    const onDoc = (e) => {
      if (startPickerOpen && startDateWrapRef.current && !startDateWrapRef.current.contains(e.target)) {
        setStartPickerOpen(false);
      }
      if (endPickerOpen && endDateWrapRef.current && !endDateWrapRef.current.contains(e.target)) {
        setEndPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [startPickerOpen, endPickerOpen]);

  // Validate date
  const validateDate = (month, day, year) => {
    if (!month || !day || !year) return { valid: true, error: '' };

    const m = parseInt(month);
    const d = parseInt(day);
    const y = parseInt(year);

    // Check month range
    if (isNaN(m) || m < 1 || m > 12) {
      return { valid: false, error: 'Month must be between 1-12' };
    }

    // Check year range
    if (isNaN(y) || y < 1900 || y > 2100) {
      return { valid: false, error: 'Please enter a valid year' };
    }

    // Days in each month
    const monthDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];

    // Check for leap year (February)
    let maxDays = monthDays[m - 1];
    if (m === 2) {
      const isLeapYear = (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0);
      if (isLeapYear) {
        maxDays = 29;
      }
    }

    // Check day range
    if (isNaN(d) || d < 1 || d > maxDays) {
      return {
        valid: false,
        error: `${monthNames[m - 1]} ${y} only has ${maxDays} days`
      };
    }

    return { valid: true, error: '' };
  };

  const parseMMDDInput = (str) => {
    const t = (str || '').trim();
    if (!t) return null;
    const parts = t.split(/[\/\-\.]/).map(p => p.trim()).filter(Boolean);
    if (parts.length !== 3) return null;
    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    if (isNaN(month) || isNaN(day) || isNaN(year)) return null;
    return { month, day, year };
  };

  const getResolvedStartParts = () => {
    const raw = (startDateDraft || '').trim();
    if (raw) {
      const p = parseMMDDInput(startDateDraft);
      if (!p) return { type: 'badFormat' };
      const m = String(p.month);
      const d = String(p.day);
      const y = String(p.year);
      const v = validateDate(m, d, y);
      if (!v.valid) return { type: 'invalidDate', error: v.error };
      return { type: 'ok', month: m, day: d, year: y };
    }
    return { type: 'ok', month: startMonth, day: startDay, year: startYear };
  };

  const getResolvedEndParts = () => {
    const raw = (endDateDraft || '').trim();
    if (raw) {
      const p = parseMMDDInput(endDateDraft);
      if (!p) return { type: 'badFormat' };
      const m = String(p.month);
      const d = String(p.day);
      const y = String(p.year);
      const v = validateDate(m, d, y);
      if (!v.valid) return { type: 'invalidDate', error: v.error };
      return { type: 'ok', month: m, day: d, year: y };
    }
    return { type: 'ok', month: endMonth, day: endDay, year: endYear };
  };

  // Validate inputs for calculation
  const validateInputs = () => {
    const newErrors = {};
    const minutes = parseInt(minutesPerDay) || 0;

    // Minutes validation
    if (!minutes) {
      newErrors.minutes = 'Minutes per day is required';
    } else if (minutes % 15 !== 0) {
      newErrors.minutes = 'Minutes must be a multiple of 15';
    } else if (minutes > 1680) {
      newErrors.minutes = 'Minutes cannot exceed 1680 (28 hours)';
    }

    const sp = getResolvedStartParts();
    if (sp.type === 'badFormat') {
      newErrors.startDate = 'Please enter a valid date (MM/DD/YYYY)';
    } else if (sp.type === 'invalidDate') {
      newErrors.startDate = sp.error;
    } else if (!sp.month || !sp.day || !sp.year) {
      newErrors.startDate = 'Start date is required';
    }

    const ep = getResolvedEndParts();
    if (ep.type === 'badFormat') {
      newErrors.endDate = 'Please enter a valid date (MM/DD/YYYY)';
    } else if (ep.type === 'invalidDate') {
      newErrors.endDate = ep.error;
    } else if (!ep.month || !ep.day || !ep.year) {
      newErrors.endDate = 'End date is required';
    }

    // Check date order and range
    if (!newErrors.startDate && !newErrors.endDate &&
        sp.type === 'ok' && ep.type === 'ok' &&
        sp.month && sp.day && sp.year && ep.month && ep.day && ep.year) {
      const startDate = new Date(parseInt(sp.year), parseInt(sp.month) - 1, parseInt(sp.day));
      const endDate = new Date(parseInt(ep.year), parseInt(ep.month) - 1, parseInt(ep.day));

      if (endDate <= startDate) {
        newErrors.endDate = 'End date must be after start date';
      }

      const oneYearLater = new Date(startDate);
      oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
      if (endDate > oneYearLater) {
        newErrors.endDate = 'End date cannot exceed one year from start';
      }
    }

    // PERS validation
    const persMonths = parseFloat(pers.months) || 0;
    const persPurchase = parseFloat(pers.purchase) || 0;
    const persInstall = parseFloat(pers.install) || 0;
    const persFee = parseFloat(pers.fee) || 0;

    // Check PERS months is whole number
    if (pers.months && persMonths !== Math.floor(persMonths)) {
      newErrors.pers = 'PERS months must be a whole number';
    }

    // Check PERS costs against state limits (example limits)
    const STATE_PERS_LIMIT = 5000; // Example state limit
    const totalPersCost = persPurchase + persInstall + (persFee * persMonths);
    if (totalPersCost > STATE_PERS_LIMIT) {
      newErrors.pers = `PERS costs cannot be greater than the current State set limits (${STATE_PERS_LIMIT})`;
    }

    // Check PERS months against service period
    if (persMonths > 0 && sp.type === 'ok' && ep.type === 'ok' &&
        sp.month && sp.day && sp.year && ep.month && ep.day && ep.year) {
      const startDate = new Date(parseInt(sp.year), parseInt(sp.month) - 1, parseInt(sp.day));
      const endDate = new Date(parseInt(ep.year), parseInt(ep.month) - 1, parseInt(ep.day));
      const monthsInPeriod = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24 * 30));

      if (persMonths > monthsInPeriod) {
        newErrors.pers = 'PERS months cannot be more than the length of service agreement';
      }
    }

    // FMS validation - only for Budget Model
    if (modelType === 'budget_model') {
      const fms = parseFloat(fmsAnnualFee) || 0;
      if (fms <= 0) {
        newErrors.fms = 'FMS fee must be greater than zero';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const blurStartDate = () => {
    setStartDateFocused(false);
    const raw = (startDateDraft || '').trim();
    if (!raw) {
      setStartMonth('');
      setStartDay('');
      setStartYear('2026');
      setStartDateDraft('');
      setErrors(prev => ({ ...prev, startDate: '' }));
      return;
    }
    const p = parseMMDDInput(startDateDraft);
    if (!p) {
      setErrors(prev => ({ ...prev, startDate: 'Please enter a valid date (MM/DD/YYYY)' }));
      return;
    }
    const m = String(p.month);
    const d = String(p.day);
    const y = String(p.year);
    const v = validateDate(m, d, y);
    if (!v.valid) {
      setErrors(prev => ({ ...prev, startDate: v.error }));
      return;
    }
    setStartMonth(m);
    setStartDay(d);
    setStartYear(y);
    setErrors(prev => ({ ...prev, startDate: '' }));
    setStartDateDraft(formatMMDDDisplay(m, d, y));
  };

  const blurEndDate = () => {
    setEndDateFocused(false);
    const raw = (endDateDraft || '').trim();
    if (!raw) {
      setEndMonth('');
      setEndDay('');
      setEndYear('2026');
      setEndDateDraft('');
      setErrors(prev => ({ ...prev, endDate: '' }));
      return;
    }
    const p = parseMMDDInput(endDateDraft);
    if (!p) {
      setErrors(prev => ({ ...prev, endDate: 'Please enter a valid date (MM/DD/YYYY)' }));
      return;
    }
    const m = String(p.month);
    const d = String(p.day);
    const y = String(p.year);
    const v = validateDate(m, d, y);
    if (!v.valid) {
      setErrors(prev => ({ ...prev, endDate: v.error }));
      return;
    }
    setEndMonth(m);
    setEndDay(d);
    setEndYear(y);
    setErrors(prev => ({ ...prev, endDate: '' }));
    setEndDateDraft(formatMMDDDisplay(m, d, y));
  };

  const applyPickedStartDate = (picked) => {
    const m = String(picked.getMonth() + 1);
    const d = String(picked.getDate());
    const y = String(picked.getFullYear());
    setStartMonth(m);
    setStartDay(d);
    setStartYear(y);
    setStartDateDraft(formatMMDDDisplay(m, d, y));
    setErrors(prev => ({ ...prev, startDate: '' }));
    setStartPickerOpen(false);
  };

  const applyPickedEndDate = (picked) => {
    const m = String(picked.getMonth() + 1);
    const d = String(picked.getDate());
    const y = String(picked.getFullYear());
    setEndMonth(m);
    setEndDay(d);
    setEndYear(y);
    setEndDateDraft(formatMMDDDisplay(m, d, y));
    setErrors(prev => ({ ...prev, endDate: '' }));
    setEndPickerOpen(false);
  };

  const openStartPicker = () => {
    const raw = (startDateDraft || '').trim();
    const p = raw ? parseMMDDInput(startDateDraft) : null;
    if (p && validateDate(String(p.month), String(p.day), String(p.year)).valid) {
      setStartCalendarView(new Date(p.year, p.month - 1, p.day));
    } else if (startMonth && startDay && startYear && validateDate(startMonth, startDay, startYear).valid) {
      setStartCalendarView(new Date(parseInt(startYear, 10), parseInt(startMonth, 10) - 1, parseInt(startDay, 10)));
    } else {
      setStartCalendarView(new Date());
    }
    setEndPickerOpen(false);
    setStartPickerOpen(true);
  };

  const openEndPicker = () => {
    const raw = (endDateDraft || '').trim();
    const p = raw ? parseMMDDInput(endDateDraft) : null;
    if (p && validateDate(String(p.month), String(p.day), String(p.year)).valid) {
      setEndCalendarView(new Date(p.year, p.month - 1, p.day));
    } else if (endMonth && endDay && endYear && validateDate(endMonth, endDay, endYear).valid) {
      setEndCalendarView(new Date(parseInt(endYear, 10), parseInt(endMonth, 10) - 1, parseInt(endDay, 10)));
    } else {
      setEndCalendarView(new Date());
    }
    setStartPickerOpen(false);
    setEndPickerOpen(true);
  };

  const getCalendarSelected = (draft, month, day, year) => {
    const raw = (draft || '').trim();
    const pr = raw ? parseMMDDInput(draft) : null;
    if (pr && validateDate(String(pr.month), String(pr.day), String(pr.year)).valid) {
      return new Date(pr.year, pr.month - 1, pr.day);
    }
    if (month && day && year && validateDate(month, day, year).valid) {
      return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    }
    return null;
  };

  const calculateResults = () => {
    if (!validateInputs()) {
      return;
    }

    const sp = getResolvedStartParts();
    const ep = getResolvedEndParts();
    if (sp.type !== 'ok' || ep.type !== 'ok' || !sp.month || !sp.day || !sp.year || !ep.month || !ep.day || !ep.year) {
      return;
    }

    const minutes = parseInt(minutesPerDay) || 0;
    const startDate = new Date(parseInt(sp.year), parseInt(sp.month) - 1, parseInt(sp.day));
    const endDate = new Date(parseInt(ep.year), parseInt(ep.month) - 1, parseInt(ep.day));

    // Calculate days
    const msPerDay = 24 * 60 * 60 * 1000;
    const totalDays = Math.round((endDate - startDate) / msPerDay) + 1;

    // Calculate units
    const unitsPerDay = minutes / 15;
    const totalUnits = Math.round(totalDays * unitsPerDay);

    // Calculate costs
    const goods = parseFloat(goodsServicesCost) || 0;
    const fms = modelType === 'budget_model' ? (parseFloat(fmsAnnualFee) || 0) : 0;
    const background = modelType === 'budget_model' ? (parseFloat(backgroundStudyFees) || 0) : 0;
    const persTotal = (parseFloat(pers.purchase) || 0) +
                      (parseFloat(pers.install) || 0) +
                      ((parseFloat(pers.fee) || 0) * (parseFloat(pers.months) || 0));

    const budgetAmounts = calculateBudgetAmounts(minutes, totalUnits, usingPersonalCare, fms, goods, persTotal, background);

    // Find holidays
    const dateStr = `${sp.year}-${String(sp.month).padStart(2, '0')}-${String(sp.day).padStart(2, '0')}`;
    const endDateStr = `${ep.year}-${String(ep.month).padStart(2, '0')}-${String(ep.day).padStart(2, '0')}`;
    const holidays = findHolidaysInRange(dateStr, endDateStr);

    // Calculate monthly allocation
    const monthlyAllocation = [];
    const current = new Date(startDate);

    while (current <= endDate) {
      const year = current.getFullYear();
      const month = current.getMonth();
      const monthName = current.toLocaleString('en-US', { month: 'long', year: 'numeric' });

      const monthStart = new Date(Math.max(current.getTime(), startDate.getTime()));
      const monthEnd = new Date(Math.min(
        new Date(year, month + 1, 0).getTime(),
        endDate.getTime()
      ));

      let daysInMonth = 0;
      let countCurrent = new Date(monthStart);
      while (countCurrent <= monthEnd) {
        daysInMonth++;
        countCurrent.setDate(countCurrent.getDate() + 1);
      }

      if (daysInMonth > 0) {
        const monthUnits = Math.round(daysInMonth * unitsPerDay);
        const monthBudget = monthUnits * budgetAmounts.ratePerUnit;

        monthlyAllocation.push({
          monthName,
          days: daysInMonth,
          units: monthUnits,
          budget: monthBudget
        });
      }

      current.setMonth(current.getMonth() + 1);
      current.setDate(1);
    }

    // Calculate time periods
    const monthsSpan = (endDate.getFullYear() - startDate.getFullYear()) * 12 +
                       (endDate.getMonth() - startDate.getMonth()) + 1;
    const weeksSpan = Math.round(totalDays / 7 * 10) / 10;

    setResults({
      totalDays,
      totalUnits,
      monthsSpan,
      weeksSpan,
      unitsPerDay: unitsPerDay.toFixed(4),
      directCare: budgetAmounts.directCare,
      enhancedDirectCare: budgetAmounts.enhancedDirectCare,
      nonEnhancedDirectCare: budgetAmounts.nonEnhancedDirectCare,
      totalBudget: budgetAmounts.baseTotalBudget,
      ratePerUnit: budgetAmounts.ratePerUnit,
      pureDirectCare: budgetAmounts.pureDirectCare,
      monthlyAllocation,
      holidays,
      goodsServicesTotal: goods,
      persTotal,
      fmsTotal: fms,
      backgroundTotal: background,
      startDate: `${sp.month}/${sp.day}/${sp.year}`,
      endDate: `${ep.month}/${ep.day}/${ep.year}`
    });

    setStartMonth(sp.month);
    setStartDay(sp.day);
    setStartYear(sp.year);
    setStartDateDraft(formatMMDDDisplay(sp.month, sp.day, sp.year));
    setEndMonth(ep.month);
    setEndDay(ep.day);
    setEndYear(ep.year);
    setEndDateDraft(formatMMDDDisplay(ep.month, ep.day, ep.year));

    setShowResults(true);
  };

  const clearAll = () => {
    setModelType('agency_model');
    setMinutesPerDay('');
    setEnhancedRate(false);
    setUsingAlternativeCare(false);
    setUsingPersonalCare(true);
    setStartMonth('');
    setStartDay('');
    setStartYear('2026');
    setEndMonth('');
    setEndDay('');
    setEndYear('2026');
    setGoodsServicesCost('');
    setFmsAnnualFee('');
    setPers({ purchase: '', install: '', fee: '', months: '' });
    setBackgroundStudyFees('');
    setShowResults(false);
    setResults(null);
    setErrors({});
    setShowEnhancedInfo(false);
    setShowMoreInfo(false);
    setStartDateDraft('');
    setEndDateDraft('');
    setStartDateFocused(false);
    setEndDateFocused(false);
    setStartPickerOpen(false);
    setEndPickerOpen(false);
  };

  const copyAgencyField = async (copyKey, value) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }

    setCopiedAgencyField(copyKey);
    window.setTimeout(() => {
      setCopiedAgencyField(current => current === copyKey ? '' : current);
    }, 1500);
  };

  const filteredAgencies = agencies.filter((agency) => {
    const query = agencySearch.trim().toLowerCase();
    if (!query) return true;
    return Object.values(agency).some(value =>
      String(value).toLowerCase().includes(query)
    );
  });
  const selectedAgency = filteredAgencies.find(agency => agency.id === selectedAgencyId) || filteredAgencies[0] || null;

  // Styles - Responsive
  const containerStyle = {
    minHeight: '100vh',
    background: '#245982',
    padding: isMobile ? '10px' : '20px',
    fontFamily: 'Arial, sans-serif'
  };

  const mainBoxStyle = {
    maxWidth: isMobile ? '100%' : '1400px',
    margin: '0 auto',
    background: '#245982',
    border: isMobile ? '2px solid white' : '3px solid white',
    borderRadius: '10px',
    padding: isMobile ? '15px' : '20px',
    position: 'relative',
    width: '100%',
    boxSizing: 'border-box',
    overflow: isMobile ? 'hidden' : 'visible'
  };

  const headerStyle = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: isMobile ? 'flex-start' : 'space-between',
    alignItems: isMobile ? 'flex-start' : 'center',
    marginBottom: isMobile ? '15px' : '20px',
    color: 'white',
    gap: isMobile ? '15px' : '0'
  };

  const titleStyle = {
    fontSize: isMobile ? '24px' : '32px',
    fontWeight: 'normal',
    margin: 0
  };

  const cardStyle = {
    background: 'white',
    border: isMobile ? '2px solid #8BC34A' : '3px solid #8BC34A',
    borderRadius: '10px',
    padding: isMobile ? '12px' : '15px',
    marginBottom: isMobile ? '12px' : '15px',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '100%',
    overflow: isMobile ? 'hidden' : 'visible',
    wordWrap: 'break-word',
    overflowWrap: 'break-word'
  };

  const inputStyle = {
    padding: isMobile ? '8px' : '5px',
    border: '1px solid #ccc',
    borderRadius: '3px',
    fontSize: isMobile ? '16px' : '14px', // Prevent zoom on iOS
    width: isMobile ? '70px' : '60px',
    marginRight: '5px'
  };

  const errorInputStyle = {
    ...inputStyle,
    border: '2px solid #f44336'
  };

  const dateTextFieldStyle = {
    padding: isMobile ? '8px' : '5px',
    border: '1px solid #ccc',
    borderRadius: '3px',
    width: isMobile ? '100%' : '168px',
    maxWidth: '100%',
    minWidth: '140px',
    boxSizing: 'border-box',
    marginRight: '5px',
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontSize: isMobile ? '16px' : '14px',
    color: '#222'
  };

  const buttonStyle = {
    padding: isMobile ? '12px 20px' : '12px 30px',
    fontSize: isMobile ? '16px' : '18px',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '25px',
    cursor: 'pointer',
    margin: isMobile ? '5px' : '0 10px',
    width: isMobile ? '100%' : 'auto',
    minWidth: isMobile ? 'auto' : '120px'
  };

  const tabButtonStyle = (tabName) => ({
    border: activeTab === tabName ? '2px solid #8BC34A' : '2px solid rgba(255,255,255,0.65)',
    background: activeTab === tabName ? 'white' : 'rgba(255,255,255,0.12)',
    color: activeTab === tabName ? '#245982' : 'white',
    borderRadius: '6px',
    padding: isMobile ? '10px 12px' : '10px 18px',
    fontSize: isMobile ? '14px' : '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    minHeight: '42px'
  });

  const agencyFieldButtonStyle = {
    border: '1px solid #8BC34A',
    background: 'white',
    color: '#245982',
    borderRadius: '5px',
    padding: '6px 8px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    minWidth: '76px',
    fontSize: '12px',
    fontWeight: 'bold',
    flexShrink: 0
  };

  const labelStyle = {
    display: 'inline-block',
    marginRight: isMobile ? '5px' : '10px',
    color: '#245982',
    fontWeight: 'bold',
    fontSize: isMobile ? '14px' : 'inherit'
  };

  const minutes = parseInt(minutesPerDay) || 0;
  const timeInfo = formatMinutesToHours(minutes);

  // Modal styles - Responsive
  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: isMobile ? '10px' : '0'
  };

  const modalContentStyle = {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: isMobile ? '20px' : '30px',
    maxWidth: isMobile ? '100%' : '600px',
    width: isMobile ? '100%' : 'auto',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    position: 'relative'
  };

  const modalTitleStyle = {
    fontSize: isMobile ? '20px' : '24px',
    fontWeight: 'bold',
    color: '#245982',
    marginBottom: '20px',
    borderBottom: '2px solid #8BC34A',
    paddingBottom: '10px'
  };

  const closeButtonStyle = {
    backgroundColor: '#245982',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    padding: isMobile ? '12px 20px' : '10px 30px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '20px',
    width: isMobile ? '100%' : 'auto'
  };

  // Enhanced Rate Modal
  const EnhancedRateModal = () => {
    if (!showEnhancedInfo) return null;

    // Different content based on model type
    const isAgencyModel = modelType === 'agency_model';
    const title = isAgencyModel ? 'Agency Model - Enhanced Rate Information' : 'Enhanced Rate Information';

    return (
      <div style={modalOverlayStyle} onClick={() => setShowEnhancedInfo(false)}>
        <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
          <h2 style={modalTitleStyle}>{title}</h2>
          <div style={{ lineHeight: '1.8', color: '#333' }}>
            <p style={{ marginBottom: '15px' }}>
              A person on a waiver and/or with home care rating EN might not use all of their CFSS hours due to their other waiver and/or home care nursing services.
            </p>
            <p>
              The enhanced rate still applies if that person is using less than 10 hours (600 minutes) per day but is eligible for 10 or more hours per day.
            </p>
          </div>
          <button style={closeButtonStyle} onClick={() => setShowEnhancedInfo(false)}>
            Close
          </button>
        </div>
      </div>
    );
  };

  // More Information Modal
  const MoreInfoModal = () => {
    if (!showMoreInfo) return null;

    return (
      <div style={modalOverlayStyle} onClick={() => setShowMoreInfo(false)}>
        <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
          <h2 style={modalTitleStyle}>About CFSS Calculator</h2>
          <div style={{ lineHeight: '1.8', color: '#333' }}>
            <p>
              The purpose of this calculator is to help people using the CFSS budget model, consultation services providers, and lead agencies quickly calculate the number of units a person has for direct care if they purchase goods, services, and/or PERS.
            </p>
            <p style={{ marginTop: '15px' }}>
              In addition, this tool helps CFSS Consultants save time by providing all the necessary information, including the number of units, total hours, total budget, total daily and weekly hours, the total number of weeks within the selected time period, as well as the total number of holidays in that period.
            </p>
          </div>
          <button style={closeButtonStyle} onClick={() => setShowMoreInfo(false)}>
            Close
          </button>
        </div>
      </div>
    );
  };

  const AgencyListTab = () => {
    const fields = [
      ['agencyName', 'Agency name'],
      ['phoneNumber', 'Phone number'],
      ['faxNumber', 'Fax number'],
      ['streetAddress', 'Street address'],
      ['city', 'City'],
      ['state', 'State'],
      ['zipCode', 'Zip code'],
      ['contactName', 'Contact name'],
      ['emailAddress', 'Email address']
    ];

    return (
      <div style={{ ...cardStyle, marginBottom: 0 }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: '12px',
          marginBottom: '16px'
        }}>
          <h2 style={{
            margin: 0,
            color: '#245982',
            fontSize: isMobile ? '20px' : '24px'
          }}>
            Agency list
          </h2>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            border: '1px solid #ccc',
            borderRadius: '6px',
            padding: '8px 10px',
            minWidth: isMobile ? 'auto' : '320px',
            boxSizing: 'border-box'
          }}>
            <Search size={18} color="#245982" />
            <input
              type="search"
              value={agencySearch}
              onChange={(e) => {
                setAgencySearch(e.target.value);
                setCopiedAgencyField('');
              }}
              placeholder="Search agency, contact, phone, email..."
              style={{
                border: 'none',
                outline: 'none',
                width: '100%',
                fontSize: isMobile ? '16px' : '14px',
                fontFamily: 'Arial, Helvetica, sans-serif'
              }}
            />
          </label>
        </div>

        {agencyLoadError && (
          <div style={{
            padding: '12px',
            marginBottom: '12px',
            border: '1px solid #f44336',
            borderRadius: '6px',
            color: '#c62828',
            background: '#ffebee'
          }}>
            {agencyLoadError}
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'minmax(240px, 360px) 1fr',
          gap: '14px',
          alignItems: 'start'
        }}>
          <div style={{
            border: '1px solid #d9e7cc',
            borderRadius: '8px',
            overflow: 'hidden',
            background: 'white'
          }}>
            <div style={{
              padding: '10px 12px',
              background: '#E8F5E9',
              borderBottom: '1px solid #d9e7cc',
              color: '#245982',
              fontWeight: 'bold'
            }}>
              {filteredAgencies.length} agenc{filteredAgencies.length === 1 ? 'y' : 'ies'}
            </div>
            <div style={{
              maxHeight: isMobile ? '260px' : '590px',
              overflowY: 'auto'
            }}>
              {filteredAgencies.map((agency) => {
                const active = selectedAgency?.id === agency.id;
                return (
                  <button
                    key={agency.id}
                    type="button"
                    onClick={() => {
                      setSelectedAgencyId(agency.id);
                      setCopiedAgencyField('');
                    }}
                    style={{
                      width: '100%',
                      border: 'none',
                      borderBottom: '1px solid #eef3ea',
                      background: active ? '#245982' : 'white',
                      color: active ? 'white' : '#222',
                      padding: '11px 12px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: 'Arial, Helvetica, sans-serif'
                    }}
                  >
                    <div style={{
                      fontSize: isMobile ? '14px' : '15px',
                      fontWeight: 'bold',
                      lineHeight: 1.3,
                      overflowWrap: 'anywhere'
                    }}>
                      {agency.agencyName}
                    </div>
                    <div style={{
                      marginTop: '3px',
                      fontSize: '12px',
                      color: active ? '#B8D4E3' : '#5c6f7c',
                      overflowWrap: 'anywhere'
                    }}>
                      {[agency.city, agency.phoneNumber].filter(Boolean).join(' - ')}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {selectedAgency ? (
            <div style={{
              border: '1px solid #d9e7cc',
              borderRadius: '8px',
              overflow: 'hidden',
              background: '#fbfdf8'
            }}>
              <div style={{
                background: '#E8F5E9',
                borderBottom: '1px solid #d9e7cc',
                padding: isMobile ? '12px' : '14px 16px'
              }}>
                <div style={{
                  color: '#245982',
                  fontSize: isMobile ? '18px' : '21px',
                  fontWeight: 'bold',
                  lineHeight: 1.25
                }}>
                  {selectedAgency.agencyName}
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
                gap: '1px',
                background: '#d9e7cc'
              }}>
                {fields.map(([key, fieldLabel]) => {
                  const value = selectedAgency[key] || '';
                  const copyKey = `${selectedAgency.id}-${key}`;
                  const isCopied = copiedAgencyField === copyKey;
                  return (
                    <div
                      key={key}
                      style={{
                        background: 'white',
                        padding: '12px',
                        minWidth: 0
                      }}
                    >
                      <div style={{
                        color: '#245982',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        marginBottom: '6px',
                        textTransform: 'uppercase'
                      }}>
                        {fieldLabel}
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '10px'
                      }}>
                        <div style={{
                          color: '#222',
                          fontSize: isMobile ? '15px' : '16px',
                          overflowWrap: 'anywhere',
                          lineHeight: 1.35
                        }}>
                          {value || ' '}
                        </div>
                        <button
                          type="button"
                          aria-label={`Copy ${fieldLabel}`}
                          title={`Copy ${fieldLabel}`}
                          disabled={!value}
                          onClick={() => copyAgencyField(copyKey, value)}
                          style={{
                            ...agencyFieldButtonStyle,
                            opacity: value ? 1 : 0.45,
                            cursor: value ? 'pointer' : 'not-allowed'
                          }}
                        >
                          {isCopied ? <Check size={15} /> : <Copy size={15} />}
                          {isCopied ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={{
              padding: '20px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              color: '#245982',
              background: '#F5F5F5',
              textAlign: 'center'
            }}>
              No agency found.
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={containerStyle}>
      {/* Add Modals */}
      <EnhancedRateModal />
      <MoreInfoModal />
      <div style={mainBoxStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <h1 style={titleStyle}>CFSS calculator</h1>
          </div>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center', 
            gap: isMobile ? '10px' : '40px',
            width: '100%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: isMobile ? '20px' : '24px', color: 'white' }}>🏢</span>
              <div>
                <div style={{ fontSize: isMobile ? '16px' : '20px', color: 'white' }}>Agency + Budget Model</div>
                <div style={{ fontSize: isMobile ? '12px' : '14px', color: '#B8D4E3' }}>v1.3</div>
              </div>
            </div>
            <div style={{ fontSize: isMobile ? '14px' : '18px', color: '#8BC34A', fontWeight: 'bold' }}>
              Created by Seo Gia Vang
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '10px',
          marginBottom: isMobile ? '12px' : '15px'
        }}>
          <button
            type="button"
            onClick={() => setActiveTab('calculator')}
            style={tabButtonStyle('calculator')}
          >
            Calculator
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('agency_list')}
            style={tabButtonStyle('agency_list')}
          >
            Agency list
          </button>
        </div>

        {activeTab === 'agency_list' ? (
          AgencyListTab()
        ) : (
          <>
        {/* Grid Layout - Responsive */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '1fr' : '1fr 1fr'), 
          gap: isMobile ? '12px' : '15px' 
        }}>
          {/* Model Type section */}
          <div style={cardStyle}>
            <div style={{ ...labelStyle, marginBottom: '10px' }}>Select Model:</div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px' }}>
                <input
                  type="radio"
                  name="modelType"
                  value="agency_model"
                  checked={modelType === 'agency_model'}
                  onChange={(e) => setModelType(e.target.value)}
                  style={{ marginRight: '5px' }}
                />
                Agency Model
              </label>
              <label style={{ display: 'block' }}>
                <input
                  type="radio"
                  name="modelType"
                  value="budget_model"
                  checked={modelType === 'budget_model'}
                  onChange={(e) => setModelType(e.target.value)}
                  style={{ marginRight: '5px' }}
                />
                Budget Model
              </label>
            </div>
          </div>

          {/* Summary Info */}
          <div style={cardStyle}>
            <div style={{ ...labelStyle, marginBottom: '10px' }}>Summary Information</div>
            {minutes > 0 && (
              <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                {results && (
                  <>
                    <div><strong>Total units:</strong> {results.totalUnits}</div>
                    <div><strong>Total days:</strong> {results.totalDays} ({results.monthsSpan} months, {results.weeksSpan} weeks)</div>
                    <div><strong>Hours per day:</strong> {formatMinutesToHours(minutesPerDay).decimal} hours</div>
                    <div><strong>Time allocation:</strong> {timeInfo.hoursMinutes}/day ({timeInfo.hoursPerWeek}h/week)</div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Time and Date Settings */}
          <div style={cardStyle}>
            <div style={{ marginBottom: '15px' }}>
              <span style={labelStyle}>Minutes per day*</span>
              <input
                type="number"
                value={minutesPerDay}
                onChange={(e) => setMinutesPerDay(e.target.value)}
                style={errors.minutes ? errorInputStyle : inputStyle}
                step="15"
              />
              <input
                type="checkbox"
                checked={enhancedRate}
                onChange={(e) => setEnhancedRate(e.target.checked)}
                style={{ marginLeft: '20px', marginRight: '5px' }}
              />
              <span>enhanced rate?</span>
              <span
                onClick={() => setShowEnhancedInfo(true)}
                style={{
                  display: 'inline-block',
                  background: '#00ACC1',
                  color: 'white',
                  borderRadius: '3px',
                  padding: '2px 8px',
                  marginLeft: '10px',
                  cursor: 'pointer'
                }}
              >?</span>
              {errors.minutes && (
                <div style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                  <AlertCircle style={{ width: '12px', height: '12px', display: 'inline' }} /> {errors.minutes}
                </div>
              )}
            </div>

            {enhancedRate && modelType === 'budget_model' && minutesPerDay >= 600 && (
              <div style={{ marginBottom: '15px', padding: '10px', background: '#E3F2FD', borderRadius: '5px' }}>
                <div style={{ marginBottom: '5px', fontWeight: 'bold', color: '#245982' }}>Using personal care?</div>
                <div style={{ color: '#245982' }}>
                  <strong>Yes</strong> - Required for enhanced rate (≥600 minutes)
                </div>
              </div>
            )}

            {modelType === 'budget_model' && minutesPerDay < 600 && (
              <div style={{ marginBottom: '15px', padding: '10px', background: '#E3F2FD', borderRadius: '5px' }}>
                <div style={{ marginBottom: '5px', fontWeight: 'bold', color: '#245982' }}>
                  {usingAlternativeCare ? 'People using CFSS on AC must use personal care' : 'Using personal care?'}
                </div>
                <label style={{ display: 'block', marginBottom: '3px' }}>
                  <input
                    type="radio"
                    checked={usingPersonalCare}
                    onChange={() => setUsingPersonalCare(true)}
                    style={{ marginRight: '5px' }}
                  />
                  Yes
                </label>
                <label style={{ display: 'block' }}>
                  <input
                    type="radio"
                    checked={!usingPersonalCare}
                    onChange={() => setUsingPersonalCare(false)}
                    style={{ marginRight: '5px' }}
                    disabled={usingAlternativeCare}
                  />
                  No
                </label>
              </div>
            )}

            <div style={{ marginBottom: '10px' }}>
              <span style={labelStyle}>Start date*</span>
              <div
                ref={startDateWrapRef}
                style={{
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '6px',
                  verticalAlign: 'middle'
                }}
              >
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="MM/DD/YYYY"
                  autoComplete="off"
                  value={startDateDraft}
                  onChange={(e) => setStartDateDraft(e.target.value)}
                  onFocus={() => {
                    setStartDateFocused(true);
                    if (!errors.startDate) {
                      setStartDateDraft(formatMMDDDisplay(startMonth, startDay, startYear));
                    }
                  }}
                  onBlur={blurStartDate}
                  style={errors.startDate ? { ...dateTextFieldStyle, border: '2px solid #f44336' } : dateTextFieldStyle}
                />
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => openStartPicker()}
                  aria-label="Open calendar for start date"
                  style={{
                    border: '1px solid #ccc',
                    background: 'white',
                    borderRadius: '4px',
                    padding: '6px 8px',
                    cursor: 'pointer',
                    color: '#245982',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <CalendarDays size={18} />
                </button>
                <CalendarDropdown
                  open={startPickerOpen}
                  anchorDate={startCalendarView}
                  onPickDay={applyPickedStartDate}
                  selectedDate={getCalendarSelected(startDateDraft, startMonth, startDay, startYear)}
                />
              </div>
              {errors.startDate && (
                <div style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                  <AlertCircle style={{ width: '12px', height: '12px', display: 'inline' }} /> {errors.startDate}
                </div>
              )}
            </div>

            <div>
              <span style={labelStyle}>End date*</span>
              <div
                ref={endDateWrapRef}
                style={{
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '6px',
                  verticalAlign: 'middle'
                }}
              >
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="MM/DD/YYYY"
                  autoComplete="off"
                  value={endDateDraft}
                  onChange={(e) => setEndDateDraft(e.target.value)}
                  onFocus={() => {
                    setEndDateFocused(true);
                    if (!errors.endDate) {
                      setEndDateDraft(formatMMDDDisplay(endMonth, endDay, endYear));
                    }
                  }}
                  onBlur={blurEndDate}
                  style={errors.endDate ? { ...dateTextFieldStyle, border: '2px solid #f44336' } : dateTextFieldStyle}
                />
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => openEndPicker()}
                  aria-label="Open calendar for end date"
                  style={{
                    border: '1px solid #ccc',
                    background: 'white',
                    borderRadius: '4px',
                    padding: '6px 8px',
                    cursor: 'pointer',
                    color: '#245982',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <CalendarDays size={18} />
                </button>
                <CalendarDropdown
                  open={endPickerOpen}
                  anchorDate={endCalendarView}
                  onPickDay={applyPickedEndDate}
                  selectedDate={getCalendarSelected(endDateDraft, endMonth, endDay, endYear)}
                />
              </div>
              {errors.endDate && (
                <div style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                  <AlertCircle style={{ width: '12px', height: '12px', display: 'inline' }} /> {errors.endDate}
                </div>
              )}
            </div>

            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={usingAlternativeCare}
                onChange={(e) => setUsingAlternativeCare(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              <span>Using Alternative Care (AC)?</span>
            </div>
          </div>

          {/* Directions */}
          <div style={cardStyle}>
            <div style={{ ...labelStyle, fontSize: '20px', marginBottom: '10px' }}>Directions</div>
            <ol style={{ margin: 0, paddingLeft: '20px', color: '#245982', lineHeight: '1.8' }}>
              <li>select your role</li>
              <li>complete all required fields (marked with *)</li>
              <li>Complete goods and services and PERS, if applicable</li>
              <li>Press calculate</li>
            </ol>
          </div>

          {/* Goods and services */}
          <div style={cardStyle}>
            <div style={{ ...labelStyle, marginBottom: '10px' }}>Goods and services</div>
            <div>
              {usingAlternativeCare && (
                <div style={{ marginBottom: '5px', color: '#c62828', fontSize: '12px' }}>
                  People using AC cannot purchase goods and services through CFSS
                </div>
              )}
              <div style={{ marginBottom: '5px', color: '#245982' }}>
                Total cost of goods and services
              </div>
              <input
                type="number"
                value={goodsServicesCost}
                onChange={(e) => setGoodsServicesCost(e.target.value)}
                style={{ ...inputStyle, width: '120px' }}
                disabled={usingAlternativeCare}
              />
            </div>
          </div>

          {/* FMS - Only show for Budget Model */}
          {modelType === 'budget_model' && (
            <div style={cardStyle}>
              <div style={{ ...labelStyle, marginBottom: '10px' }}>Financial Management Services (FMS)</div>
              <div>
                <div style={{ marginBottom: '5px', color: '#245982' }}>
                  Total annual FMS fee
                </div>
                <input
                  type="number"
                  value={fmsAnnualFee}
                  onChange={(e) => setFmsAnnualFee(e.target.value)}
                  style={{ ...inputStyle, width: '120px' }}
                />
                {errors.fms && (
                  <div style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                    <AlertCircle style={{ width: '12px', height: '12px', display: 'inline' }} /> {errors.fms}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PERS */}
          <div style={cardStyle}>
            <div style={{ ...labelStyle, marginBottom: '10px' }}>Personal emergency response system (PERS)</div>
            {usingAlternativeCare && (
              <div style={{ marginBottom: '5px', color: '#c62828', fontSize: '12px' }}>
                People using AC cannot purchase PERS through CFSS
              </div>
            )}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', 
              gap: isMobile ? '10px' : '10px' 
            }}>
              <div>
                <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#245982', marginBottom: '3px' }}>Purchase</div>
                <input
                  type="number"
                  value={pers.purchase}
                  onChange={(e) => setPers({...pers, purchase: e.target.value})}
                  style={{ ...(errors.pers ? errorInputStyle : inputStyle), width: '100%' }}
                  disabled={usingAlternativeCare}
                />
              </div>
              <div>
                <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#245982', marginBottom: '3px' }}>Install</div>
                <input
                  type="number"
                  value={pers.install}
                  onChange={(e) => setPers({...pers, install: e.target.value})}
                  style={{ ...(errors.pers ? errorInputStyle : inputStyle), width: '100%' }}
                  disabled={usingAlternativeCare}
                />
              </div>
              <div>
                <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#245982', marginBottom: '3px' }}>Fee</div>
                <input
                  type="number"
                  value={pers.fee}
                  onChange={(e) => setPers({...pers, fee: e.target.value})}
                  style={{ ...(errors.pers ? errorInputStyle : inputStyle), width: '100%' }}
                  disabled={usingAlternativeCare}
                />
              </div>
              <div>
                <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#245982', marginBottom: '3px' }}>Months</div>
                <input
                  type="number"
                  value={pers.months}
                  onChange={(e) => setPers({...pers, months: e.target.value})}
                  style={{ ...(errors.pers ? errorInputStyle : inputStyle), width: '100%' }}
                  disabled={usingAlternativeCare}
                />
              </div>
            </div>
            {errors.pers && (
              <div style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                <AlertCircle style={{ width: '12px', height: '12px', display: 'inline' }} /> {errors.pers}
              </div>
            )}
          </div>

          {/* Background Study - Only show for Budget Model */}
          {modelType === 'budget_model' && (
            <div style={cardStyle}>
              <div style={{ ...labelStyle, marginBottom: '10px' }}>Failed background study</div>
              {usingPersonalCare ? (
                <div>
                  <div style={{ marginBottom: '5px', color: '#245982', fontSize: '13px' }}>
                    Only complete if you have received notice that a worker failed a background study
                  </div>
                  <div>
                    <span style={{ color: '#245982', marginRight: '10px' }}>Number of failed studies:</span>
                    <input
                      type="number"
                      value={backgroundStudyFees}
                      onChange={(e) => setBackgroundStudyFees(e.target.value)}
                      style={errors.background ? errorInputStyle : inputStyle}
                    />
                  </div>
                  {errors.background && (
                    <div style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                      <AlertCircle style={{ width: '12px', height: '12px', display: 'inline' }} /> {errors.background}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{
                  padding: '15px',
                  background: '#F5F5F5',
                  borderRadius: '5px',
                  color: '#245982',
                  fontSize: '14px'
                }}>
                  Background studies do not apply if you're not using personal care.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Buttons - Responsive */}
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'center', 
          marginTop: isMobile ? '15px' : '20px',
          gap: isMobile ? '10px' : '0'
        }}>
          <button
            style={{ ...buttonStyle, background: '#5A9FD4', color: 'white' }}
            onClick={() => setShowMoreInfo(true)}
          >
            {isMobile ? 'More Information' : <>More<br />Information</>}
          </button>
          <button
            style={{ ...buttonStyle, background: '#8BC34A', color: 'white' }}
            onClick={calculateResults}
          >
            Calculate
          </button>
          <button
            style={{ ...buttonStyle, background: 'white', color: '#245982', border: '2px solid #245982' }}
            onClick={clearAll}
          >
            Clear
          </button>
        </div>

        {/* Results Section */}
        {showResults && results && (
          <div style={{ 
            ...cardStyle, 
            marginTop: '20px',
            overflow: 'visible', // Allow tables to scroll
            overflowX: 'visible'
          }}>
            <h2 style={{ 
              color: '#245982', 
              borderBottom: '2px solid #8BC34A', 
              paddingBottom: '10px',
              fontSize: isMobile ? '20px' : 'inherit'
            }}>
              Calculation Results
            </h2>

            {modelType === 'agency_model' ? (
              // Agency Model Results - Simplified - Responsive
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '1fr' : '1fr 1fr'), 
                gap: isMobile ? '15px' : '20px', 
                marginTop: '20px',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}>
                {/* Summary Column */}
                <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                  <h3 style={{ color: '#245982', marginBottom: '10px', fontSize: isMobile ? '16px' : 'inherit' }}>Summary</h3>
                  <div style={{ 
                    lineHeight: '1.8', 
                    fontSize: '14px',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    width: '100%',
                    maxWidth: '100%'
                  }}>
                    <div><strong>Service Period:</strong> {results.startDate} - {results.endDate}</div>
                    <div><strong>Total units:</strong> {results.totalUnits}</div>
                    <div><strong>Units per day:</strong> {results.unitsPerDay}</div>
                    <div><strong>Total days:</strong> {results.totalDays}</div>
                    <div><strong>Hours per day:</strong> {formatMinutesToHours(minutesPerDay).decimal} hours</div>
                    <div><strong>Minutes per day:</strong> {minutesPerDay} minutes</div>
                    <div><strong>Time allocation:</strong> {formatMinutesToHours(minutesPerDay).hoursMinutes}/day ({formatMinutesToHours(minutesPerDay).hoursPerWeek}h/week)</div>
                    <div style={{ 
                      marginTop: '15px', 
                      padding: '10px', 
                      background: '#F5F5F5', 
                      borderRadius: '5px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box'
                    }}>
                      <div><strong>Goods/Services:</strong> ${results.goodsServicesTotal.toFixed(2)}</div>
                      <div><strong>PERS:</strong> ${results.persTotal.toFixed(2)}</div>
                    </div>
                  </div>
                </div>

                {/* Monthly Allocation Column */}
                <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                  <h3 style={{ color: '#245982', marginBottom: '10px', fontSize: isMobile ? '16px' : 'inherit' }}>Monthly Allocation</h3>
                  <div style={{ 
                    maxHeight: '400px', 
                    overflowY: 'auto',
                    overflowX: isMobile ? 'auto' : 'visible',
                    WebkitOverflowScrolling: 'touch',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}>
                    <table style={{ 
                      width: '100%', 
                      borderCollapse: 'collapse', 
                      fontSize: isMobile ? '11px' : '13px',
                      tableLayout: 'auto',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word'
                    }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #245982' }}>
                          <th style={{ 
                            textAlign: 'left', 
                            padding: isMobile ? '6px 3px' : '5px', 
                            color: '#245982',
                            wordWrap: 'break-word',
                            maxWidth: isMobile ? '80px' : 'auto'
                          }}>Month</th>
                          <th style={{ 
                            textAlign: 'center', 
                            padding: isMobile ? '6px 3px' : '5px', 
                            color: '#245982',
                            wordWrap: 'break-word'
                          }}>Days</th>
                          <th style={{ 
                            textAlign: 'center', 
                            padding: isMobile ? '6px 3px' : '5px', 
                            color: '#245982',
                            wordWrap: 'break-word',
                            fontSize: isMobile ? '10px' : 'inherit'
                          }}>Units/Day</th>
                          <th style={{ 
                            textAlign: 'center', 
                            padding: isMobile ? '6px 3px' : '5px', 
                            color: '#245982',
                            wordWrap: 'break-word'
                          }}>Units</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.monthlyAllocation.map((month, index) => (
                          <tr key={index} style={{ borderBottom: '1px solid #ddd' }}>
                            <td style={{ 
                              padding: isMobile ? '6px 3px' : '5px',
                              wordWrap: 'break-word',
                              maxWidth: isMobile ? '80px' : 'auto',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>{month.monthName}</td>
                            <td style={{ 
                              textAlign: 'center', 
                              padding: isMobile ? '6px 3px' : '5px',
                              wordWrap: 'break-word'
                            }}>{month.days}</td>
                            <td style={{ 
                              textAlign: 'center', 
                              padding: isMobile ? '6px 3px' : '5px',
                              wordWrap: 'break-word',
                              fontSize: isMobile ? '10px' : 'inherit'
                            }}>{results.unitsPerDay}</td>
                            <td style={{ 
                              textAlign: 'center', 
                              padding: isMobile ? '6px 3px' : '5px',
                              wordWrap: 'break-word',
                              whiteSpace: isMobile ? 'nowrap' : 'normal'
                            }}>{month.units}</td>
                          </tr>
                        ))}
                        <tr style={{ borderTop: '2px solid #245982', fontWeight: 'bold' }}>
                          <td style={{ 
                            padding: isMobile ? '6px 3px' : '5px',
                            wordWrap: 'break-word'
                          }}>Total</td>
                          <td style={{ 
                            textAlign: 'center', 
                            padding: isMobile ? '6px 3px' : '5px',
                            wordWrap: 'break-word'
                          }}>{results.totalDays}</td>
                          <td style={{ 
                            textAlign: 'center', 
                            padding: isMobile ? '6px 3px' : '5px',
                            wordWrap: 'break-word'
                          }}>-</td>
                          <td style={{ 
                            textAlign: 'center', 
                            padding: isMobile ? '6px 3px' : '5px',
                            wordWrap: 'break-word',
                            whiteSpace: isMobile ? 'nowrap' : 'normal'
                          }}>{results.totalUnits}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              // Budget Model Results - Full details - Responsive
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '1fr' : '1fr 1fr 1fr'), 
                gap: isMobile ? '15px' : '20px', 
                marginTop: '20px',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}>
                {/* Summary Column */}
                <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                  <h3 style={{ color: '#245982', marginBottom: '10px', fontSize: isMobile ? '16px' : 'inherit' }}>Budget Summary</h3>
                  <div style={{ 
                    lineHeight: '1.8', 
                    fontSize: '14px',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    width: '100%',
                    maxWidth: '100%'
                  }}>
                    <div><strong>Service Period:</strong> {results.startDate} - {results.endDate}</div>
                    <div><strong>Total Days:</strong> {results.totalDays} ({results.monthsSpan} months, {results.weeksSpan} weeks)</div>
                    <div><strong>Total units:</strong> {results.totalUnits}</div>
                    <div><strong>Units per Day:</strong> {results.unitsPerDay}</div>
                    <div><strong>Hours per day:</strong> {formatMinutesToHours(minutesPerDay).decimal} hours</div>
                    <div><strong>Minutes per day:</strong> {minutesPerDay} minutes</div>
                    <div><strong>Time allocation:</strong> {formatMinutesToHours(minutesPerDay).hoursMinutes}/day ({formatMinutesToHours(minutesPerDay).hoursPerWeek}h/week)</div>

                    <div style={{ marginTop: '15px', padding: '10px', background: '#F5F5F5', borderRadius: '5px' }}>
                      <div><strong>Goods/Services:</strong> ${results.goodsServicesTotal.toFixed(2)}</div>
                      <div><strong>PERS:</strong> ${results.persTotal.toFixed(2)}</div>
                      <div><strong>FMS Fees:</strong> ${results.fmsTotal.toFixed(2)}</div>
                      <div><strong>Background Study:</strong> ${results.backgroundTotal.toFixed(2)}</div>
                    </div>

                    {results.enhancedDirectCare && results.nonEnhancedDirectCare && (
                      <div style={{ marginTop: '10px', padding: '10px', background: '#E3F2FD', borderRadius: '5px' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Enhanced Rate Options:</div>
                        <div style={{ fontSize: '13px' }}>
                          <div>Enhanced care: ${results.enhancedDirectCare.toFixed(2)}</div>
                          <div>Non-enhanced: ${results.nonEnhancedDirectCare.toFixed(2)}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Monthly Allocation Column */}
                <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                  <h3 style={{ color: '#245982', marginBottom: '10px', fontSize: isMobile ? '16px' : 'inherit' }}>Monthly Allocation</h3>
                  <div style={{ 
                    maxHeight: '400px', 
                    overflowY: 'auto',
                    overflowX: isMobile ? 'auto' : 'visible',
                    WebkitOverflowScrolling: 'touch',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}>
                    <table style={{ 
                      width: '100%', 
                      borderCollapse: 'collapse', 
                      fontSize: isMobile ? '11px' : '13px',
                      tableLayout: 'auto',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word'
                    }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #245982' }}>
                          <th style={{ 
                            textAlign: 'left', 
                            padding: isMobile ? '6px 3px' : '5px', 
                            color: '#245982',
                            wordWrap: 'break-word',
                            maxWidth: isMobile ? '70px' : 'auto'
                          }}>Month</th>
                          <th style={{ 
                            textAlign: 'center', 
                            padding: isMobile ? '6px 3px' : '5px', 
                            color: '#245982',
                            wordWrap: 'break-word'
                          }}>Days</th>
                          <th style={{ 
                            textAlign: 'center', 
                            padding: isMobile ? '6px 3px' : '5px', 
                            color: '#245982',
                            wordWrap: 'break-word',
                            fontSize: isMobile ? '10px' : 'inherit'
                          }}>Units/Day</th>
                          <th style={{ 
                            textAlign: 'center', 
                            padding: isMobile ? '6px 3px' : '5px', 
                            color: '#245982',
                            wordWrap: 'break-word'
                          }}>Units</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.monthlyAllocation.map((month, index) => (
                          <tr key={index} style={{ borderBottom: '1px solid #ddd' }}>
                            <td style={{ 
                              padding: isMobile ? '6px 3px' : '5px',
                              wordWrap: 'break-word',
                              maxWidth: isMobile ? '70px' : 'auto',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>{month.monthName}</td>
                            <td style={{ 
                              textAlign: 'center', 
                              padding: isMobile ? '6px 3px' : '5px',
                              wordWrap: 'break-word'
                            }}>{month.days}</td>
                            <td style={{ 
                              textAlign: 'center', 
                              padding: isMobile ? '6px 3px' : '5px',
                              wordWrap: 'break-word',
                              fontSize: isMobile ? '10px' : 'inherit'
                            }}>{results.unitsPerDay}</td>
                            <td style={{ 
                              textAlign: 'center', 
                              padding: isMobile ? '6px 3px' : '5px',
                              wordWrap: 'break-word',
                              whiteSpace: isMobile ? 'nowrap' : 'normal'
                            }}>{month.units}</td>
                          </tr>
                        ))}
                        <tr style={{ borderTop: '2px solid #245982', fontWeight: 'bold' }}>
                          <td style={{ 
                            padding: isMobile ? '6px 3px' : '5px',
                            wordWrap: 'break-word'
                          }}>TOTAL</td>
                          <td style={{ 
                            textAlign: 'center', 
                            padding: isMobile ? '6px 3px' : '5px',
                            wordWrap: 'break-word'
                          }}>{results.totalDays}</td>
                          <td style={{ 
                            textAlign: 'center', 
                            padding: isMobile ? '6px 3px' : '5px',
                            wordWrap: 'break-word'
                          }}>-</td>
                          <td style={{ 
                            textAlign: 'center', 
                            padding: isMobile ? '6px 3px' : '5px',
                            wordWrap: 'break-word',
                            whiteSpace: isMobile ? 'nowrap' : 'normal'
                          }}>{results.totalUnits}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Holidays Column */}
                <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                  <h3 style={{ color: '#245982', marginBottom: '10px', fontSize: isMobile ? '16px' : 'inherit' }}>Holiday Analysis</h3>
                  {results.holidays && results.holidays.length > 0 ? (
                    <div>
                      <div style={{ marginBottom: '10px', color: '#245982', fontWeight: 'bold' }}>
                        {results.holidays.length} holiday{results.holidays.length > 1 ? 's' : ''} found
                      </div>
                      <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                        {results.holidays.map((holiday, index) => (
                          <div key={index} style={{
                            marginBottom: '10px',
                            padding: '8px',
                            background: '#FFF3E0',
                            borderRadius: '5px',
                            fontSize: '13px'
                          }}>
                            <div style={{ fontWeight: 'bold', color: '#E65100' }}>{holiday.name}</div>
                            <div style={{ color: '#666', fontSize: '12px' }}>{holiday.dateStr}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{
                        marginTop: '10px',
                        padding: '10px',
                        background: '#FFF3E0',
                        borderRadius: '5px',
                        fontSize: '12px'
                      }}>
                        <strong>Note:</strong> Service planning may need adjustments on these dates
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: '10px', background: '#E8F5E9', borderRadius: '5px' }}>
                      <div style={{ color: '#2E7D32', fontWeight: 'bold' }}>✓ No holidays in period</div>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                        Regular service schedule can be maintained
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Detailed Breakdown - Only for Budget Model */}
            {modelType === 'budget_model' && (
              <div style={{ 
                marginTop: '20px', 
                padding: '10px', 
                background: '#F5F5F5', 
                borderRadius: '5px', 
                fontSize: '12px',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                wordWrap: 'break-word',
                overflowWrap: 'break-word'
              }}>
                <strong>Calculation Details:</strong>
                <div style={{ 
                  marginTop: '5px', 
                  lineHeight: '1.6',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  width: '100%',
                  maxWidth: '100%'
                }}>
                  <div>Pure direct care: {results.totalUnits} units × ${results.ratePerUnit.toFixed(6)} = ${results.pureDirectCare.toFixed(2)}</div>
                  <div>Base total budget: ${results.pureDirectCare.toFixed(2)} + $630 admin = ${results.totalBudget.toFixed(2)}</div>
                  <div>Final direct care: ${results.totalBudget.toFixed(2)} - ${results.fmsTotal.toFixed(2)} FMS - ${(results.goodsServicesTotal + results.persTotal + results.backgroundTotal).toFixed(2)} other = ${results.directCare.toFixed(2)}</div>
                </div>
              </div>
            )}
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}
